import React, { useState, useRef } from 'react';
import { useClassName, useWindowSize } from 'common/hooks';
import { SVGIcon as Icon, optimizeContentfulImageUrl } from 'common/components';
import { CTA_TYPES, STANDARD_EVENTS } from 'utils/analytics/trackV2';
import useCustomStyle from '../../hooks';
import { Pagination, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import './card-u.less';
import Link from 'public/screens/CustomLandingPage/Components/Link';
import Actions from 'public/screens/CustomLandingPage/Components/Actions';
import InfoModal from 'public/screens/CustomLandingPage/Components/InfoModal';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { usePagination } from 'public/screens/Home/Components/usePagination';
import { getStylesByPrefix } from '../../utils';

export function CardInfoModal({ cta, infoModal, ctaStyles, handleTrack }) {
  const linkClassNames = useClassName('CLPLink');
  const [, ctaStyle] = useCustomStyle(cta?.fields?.settings);
  const modalTriggerStyles = {
    border: 0,
    fontFamily: 'inherit',
    ...getStylesByPrefix('--link-', ctaStyle),
    ...ctaStyles,
  };

  const handleOpen = () => {
    if (!handleTrack || !cta) return;

    handleTrack({
      event: STANDARD_EVENTS.click,
      params: {
        label: cta.fields.label,
        cta_type: CTA_TYPES.modal,
        destination_url: cta.fields.url,
      },
    });
  };

  return (
    <InfoModal
      component={infoModal}
      onOpen={handleOpen}
      trigger={
        cta ? (
          <button
            type="button"
            style={modalTriggerStyles}
            className={linkClassNames([
              cta.fields.theme !== 'other' && 'container',
              cta.fields.theme,
            ])}>
            {cta.fields.label}
          </button>
        ) : null
      }
    />
  );
}

export default function CardTypeU({
  item,
  refs,
  handleTrack,
  isActive,
  onSelect,
}) {
  const classNames = useClassName('CLPCardTypeU');
  const [setRef, style] = useCustomStyle(item.fields.settings);
  const ctas = item.fields.links || [];
  const title = item.fields.title;
  const subtitle = item.fields.subtitle;
  const description = item.fields.description;

  const itemDetails = item.fields.itemDetails;
  const carousel = item.fields.carousel;
  const infoModal = item.fields.infoModal;
  const pagination = usePagination({});
  const { isMobile, isTablet, isDesktop } = useWindowSize();
  const [hasMultipleSlides, setHasMultipleSlides] = useState(false);
  const containerRef = useRef(null);

  const isAccordionMode = Boolean(onSelect);
  const stateClass = isAccordionMode ? (isActive ? 'open' : 'closed') : null;
  const ctaStyles = getStylesByPrefix('--info-cta-', style);

  const handleSwiperInit = (swiper) => {
    const bullets = swiper.pagination.bullets;
    setHasMultipleSlides(bullets.length > 1);
  };

  const handleClick = () => {
    if (isActive) return;
    onSelect?.();

    if ((isMobile || isTablet) && containerRef.current) {
      setTimeout(() => {
        const element = containerRef.current;
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        setRef(node);
      }}
      className={classNames(['container', `container--${stateClass}`])}
      onClick={handleClick}
      style={{}}>
      {
        <>
          {carousel && (
            <div className={classNames('swiper')}>
              <Swiper
                {...pagination}
                autoCorrect="on"
                modules={[Pagination, Autoplay]}
                centeredSlides={isMobile || isTablet ? true : false}
                spaceBetween={0}
                slidesPerView={1}
                speed={500}
                onSwiper={handleSwiperInit}
                preventClicksPropagation={true}
                className={classNames('carousel')}
                autoplay={{ delay: 3000, disableOnInteraction: false }}
                loop={hasMultipleSlides}>
                {(carousel?.fields?.slides || []).map((slide, index) => {
                  return (
                    <SwiperSlide
                      key={index}
                      className={classNames('carousel__slide')}>
                      {isMobile && (
                        <img
                          src={optimizeContentfulImageUrl(
                            slide.fields.mobile.fields.file.url,
                            slide.fields.mobile.fields.file.contentType
                          )}
                          alt={slide.fields.mobile.fields.description}
                        />
                      )}
                      {isTablet && (
                        <img
                          src={optimizeContentfulImageUrl(
                            slide.fields.tablet.fields.file.url,
                            slide.fields.tablet.fields.file.contentType
                          )}
                          alt={slide.fields.tablet.fields.description}
                        />
                      )}
                      {isDesktop && (
                        <img
                          src={optimizeContentfulImageUrl(
                            slide.fields.desktop.fields.file.url,
                            slide.fields.desktop.fields.file.contentType
                          )}
                          alt={slide.fields.desktop.fields.description}
                        />
                      )}
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>
          )}
          <div className={classNames('info')}>
            <div className={classNames('info-subtitle')}>{subtitle}</div>
            <div className={classNames('info-title')}>{title}</div>
            {description && (
              <div className={classNames('info-description')}>
                {documentToReactComponents(description)}
              </div>
            )}
            {itemDetails && (
              <div className={classNames('info-item-details')}>
                {itemDetails?.fields?.items.map((detail) => (
                  <div
                    key={detail.sys.id}
                    className={classNames('info-item-details-row')}>
                    {detail.fields.icon?.fields?.file && (
                      <div className={classNames('info-item-details-row-icon')}>
                        <img
                          src={optimizeContentfulImageUrl(
                            detail.fields.icon.fields.file.url,
                            detail.fields.icon.fields.file.contentType
                          )}
                          alt={detail.fields.icon.fields.title}
                        />
                      </div>
                    )}
                    <div className={classNames('info-item-details-row-title')}>
                      {detail.fields.title}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(ctas.length > 0 || infoModal) && (
              <div className={classNames('info-cta')}>
                <Actions>
                  {infoModal ? (
                    <CardInfoModal
                      cta={ctas[0]}
                      ctaStyles={ctaStyles}
                      handleTrack={handleTrack}
                      infoModal={infoModal}
                    />
                  ) : (
                    ctas[0] && (
                      <Link
                        key={ctas[0].sys.id}
                        style={ctaStyles}
                        component={ctas[0]}
                        refs={refs}
                        handleTrack={handleTrack}
                      />
                    )
                  )}
                  {ctas.slice(1).map((cta) => (
                    <Link
                      key={cta.sys.id}
                      style={ctaStyles}
                      component={cta}
                      refs={refs}
                      handleTrack={handleTrack}
                    />
                  ))}
                </Actions>
              </div>
            )}
          </div>
          {isAccordionMode && (
            <div className={classNames('plus-button')}>
              <Icon name="add-minimal" size="small" />
            </div>
          )}
        </>
      }
    </div>
  );
}
